/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';

import { Badge, DividerNew, Heading, useFlags } from 'sarsaparilla';

import { INVENTORY_KEYS } from '../../constants/filters/inventoryTypes';
import {
    countUiFilterSelections,
    getUiFilterSelections,
    type UiFilterOption,
} from '../../utils/filters';

import { ActivityTagsFilters } from './filter-sets/ActivityTagsFilters';
import { AdditionalPreferencesFilters } from './filter-sets/AdditionalPreferencesFilters';
import { CampingFilters } from './filter-sets/CampingFilters';
import { InventoryTypeFilters } from './filter-sets/InventoryTypeFilters';
import { StartTimeFilters } from './filter-sets/StartTimeFilters';
import { TourDurationFilters } from './filter-sets/TourDurationFilters';
import { TourTypeFilters } from './filter-sets/TourTypeFilters';
import type { PendingFilters, PendingFiltersAction } from './pendingFilters';
import styles from './SearchFiltersDialog.module.scss';

const TOUR_FILTER_NAMES = [
    'tourTypes',
    'tourAccessible',
    'tourDifficulty',
    'tourDuration',
    'tourPrice',
    'tourTime',
    'tourTimes',
];

interface SearchFiltersSectionsProps {
    /** The filters as the user has set them in the dialog */
    pending: PendingFilters;
    /** Whether the search has a check-in/check-out date range */
    hasDates: boolean;
    onChange: React.Dispatch<PendingFiltersAction>;
}

/**
 * The filter sections of the filters dialog. Changes only update the pending
 * filters; nothing is searched until the dialog's results button is pressed.
 */
export function SearchFiltersSections({
    pending,
    hasDates,
    onChange,
}: SearchFiltersSectionsProps) {
    const { activityTagsMvp } = useFlags();
    const { fg, uiFilters } = pending;

    const isCampingSearch = fg.includes('camping');
    const isDayUseSearch = fg.includes('dayuse');
    const isTicketsSearch = fg.includes('tickets');
    const hasNoInventory = !INVENTORY_KEYS.some((inventoryType) =>
        fg.includes(inventoryType)
    );
    const showCampingFilters = isCampingSearch || isDayUseSearch || hasNoInventory;
    const showTourFilters = isTicketsSearch || hasNoInventory;
    const showActivityTags = !!activityTagsMvp?.enabled;

    const onFilterGroupsChange = React.useCallback(
        (nextFg: string[]) => onChange({ type: 'setFilterGroups', fg: nextFg }),
        [onChange]
    );

    const onFilterToggle = (filterName: string, option: UiFilterOption) => {
        onChange({ type: 'toggleUiFilter', filterName, option });
    };

    const onFilterChange = (filterName: string, option: UiFilterOption) => {
        onChange({ type: 'setUiFilter', filterName, option });
    };

    const onFilterRemove = (filterName: string, option: UiFilterOption) => {
        onChange({ type: 'removeUiFilter', filterName, option });
    };

    const activityTagsFilterCount = countUiFilterSelections(uiFilters, ['activityTags']);
    const tourFilterCount = countUiFilterSelections(uiFilters, TOUR_FILTER_NAMES);

    // The dialog renders in a portal, so the wrapper classes bring along the
    // desktop panel styles these filter components are built on
    return (
        <div className={cx('search-filters-dropdown-wrap', styles.Filters)}>
            <div className="search-filters-dropdown-body">
                <section className="search-filters-type-wrap">
                    <div className="search-filters-filter-container">
                        <InventoryTypeFilters
                            fg={fg}
                            onFilterGroupsChange={onFilterGroupsChange}
                        />
                    </div>
                </section>

                {showCampingFilters && (
                    <CampingFilters
                        fg={fg}
                        uiFilters={uiFilters}
                        onFilterGroupsChange={onFilterGroupsChange}
                        hasDates={hasDates}
                    />
                )}

                {(isCampingSearch || hasNoInventory) && showActivityTags && (
                    <section className="search-filters-type-wrap">
                        <div className="search-filters-section-header rounded-layout">
                            <Heading
                                headingLevel={2}
                                appearance="h5"
                                className={styles.activitiesHeading}
                            >
                                Nearby Activities <Badge appearance="info">BETA</Badge>
                            </Heading>
                            <span className="search-filters-section-header__count">
                                {`${activityTagsFilterCount} ${
                                    activityTagsFilterCount === 1 ? 'Filter' : 'Filters'
                                } Selected`}
                            </span>
                        </div>
                        <div className="search-filters-filter-container">
                            <p className={styles.activitiesDisclaimer}>
                                Please confirm desired activity is available at location
                                before booking.
                            </p>

                            <DividerNew className="mb-2" />

                            <ActivityTagsFilters
                                uiFilters={{
                                    activityTags: getUiFilterSelections(
                                        uiFilters,
                                        'activityTags'
                                    ),
                                }}
                                onFilterToggle={onFilterToggle}
                            />
                        </div>
                    </section>
                )}

                {showTourFilters && (
                    <section className="search-filters-type-wrap">
                        <div className="search-filters-section-header rounded-layout">
                            <Heading headingLevel={2} appearance="h5">
                                Tickets & Tours
                            </Heading>
                            <span className="search-filters-section-header__count">
                                {`${tourFilterCount} ${
                                    tourFilterCount === 1 ? 'Filter' : 'Filters'
                                } Selected`}
                            </span>
                        </div>
                        <div className="search-filters-filter-container">
                            <StartTimeFilters
                                uiFilters={{
                                    tourTimes: getUiFilterSelections(
                                        uiFilters,
                                        'tourTimes'
                                    ),
                                }}
                                onFilterToggle={onFilterToggle}
                            />

                            <DividerNew className="my-4" />

                            <TourTypeFilters
                                uiFilters={{
                                    tourTypes: getUiFilterSelections(
                                        uiFilters,
                                        'tourTypes'
                                    ),
                                }}
                                onFilterToggle={onFilterToggle}
                            />

                            <DividerNew className="my-4" />

                            <TourDurationFilters
                                uiFilters={uiFilters}
                                onFilterChange={onFilterChange}
                                onFilterRemove={onFilterRemove}
                            />
                        </div>
                    </section>
                )}

                <AdditionalPreferencesFilters
                    uiFilters={uiFilters}
                    onFilterToggle={onFilterToggle}
                    hideActivities={showActivityTags}
                />
            </div>
        </div>
    );
}

// cSpell:ignore dayuse
